import React, { useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { Spinner } from 'react-bootstrap'
import { PageTitle } from '../../../_metronic/layout/core'
import { getErrorCode } from '../../common/api/errors'
import { payoutTicketInfoUrl } from '../../common/PayoutTokenLink'
import { payoutRefusalCardUrl } from '../payout-tickets/refusals/core/consts'
import RefusalFacts, { FactRow } from './components/RefusalFacts'
import { BORDER, INK, MUTED, NO_BREADCRUMBS, RAW_BLOCK } from './core/consts'
import { getFlowControlRefusalCard } from './core/refusalApi'
import { FlowControlRefusalCard } from './core/refusal.types'

const OutcomeLink: React.FC<{ card: FlowControlRefusalCard }> = ({ card }) => {
  if (card.outcome === 'MERCHANT_REFUSED' && card.cascadeRefusalId !== null) {
    return (
      <Link to={payoutRefusalCardUrl(card.cascadeRefusalId)} className='btn btn-sm btn-light-primary'>
        Отказ в создании выплаты
      </Link>
    )
  }
  if (card.payoutToken) {
    return (
      <Link to={payoutTicketInfoUrl(card.payoutToken)} className='btn btn-sm btn-light-primary'>
        Карточка выплаты
      </Link>
    )
  }

  return null
}

/** Matching's refusal of a payout: the attempt, the decision broken down, and where it led. */
const FlowControlRefusalCardPage: React.FC = () => {
  const { id } = useParams<{ id: string }>()
  const [card, setCard] = useState<FlowControlRefusalCard | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [rawOpen, setRawOpen] = useState(false)

  useEffect(() => {
    if (!id) return

    let cancelled = false
    setCard(null)
    setError(null)

    getFlowControlRefusalCard(id)
      .then(({ data }) => {
        if (cancelled) return
        if (!data.success) {
          setError(data.errors.general_error ?? 'Не удалось загрузить отказ')
          return
        }
        setCard(data.data)
      })
      .catch((e) => {
        if (cancelled) return
        setError(getErrorCode(e) === 'FLOW_CONTROL_REFUSAL_NOT_FOUND' ? 'Отказ не найден' : 'Не удалось загрузить отказ')
      })

    return () => {
      cancelled = true
    }
  }, [id])

  return (
    <>
      <PageTitle breadcrumbs={NO_BREADCRUMBS}>Отказ мэтчинга</PageTitle>

      {error && (
        <div className='card' style={{ padding: '24px 28px' }}>
          <div className='text-danger' style={{ fontSize: 14 }}>
            {error}
          </div>
        </div>
      )}

      {!error && !card && (
        <div className='d-flex justify-content-center' style={{ padding: 48 }}>
          <Spinner animation='border' variant='primary' />
        </div>
      )}

      {!error && card && (
        <>
          <div className='d-flex align-items-center' style={{ gap: 12, marginBottom: 18, flexWrap: 'wrap' }}>
            <h1 style={{ margin: 0, fontSize: 20, fontWeight: 600, color: INK }}>Отказ мэтчинга</h1>
            <span className={card.outcome === 'OVERRIDDEN' ? 'badge badge-light-warning' : 'badge badge-light-danger'}>{card.stageTitle}</span>
            <span style={{ fontSize: 13, color: MUTED }}>{card.outcomeTitle}</span>
            <div style={{ flexGrow: 1 }} />
            <OutcomeLink card={card} />
          </div>

          <div className='d-flex' style={{ gap: 18, alignItems: 'flex-start' }}>
            <div style={{ width: 396, flexShrink: 0 }}>
              <RefusalFacts title='Попытка' facts={card.facts} />
            </div>

            <div className='card' style={{ flexGrow: 1, minWidth: 0 }}>
              <div
                className='d-flex align-items-center'
                style={{ minHeight: 56, padding: '0 28px', borderBottom: `1px solid ${BORDER}` }}
              >
                <h2 style={{ margin: 0, fontSize: 15, fontWeight: 600, color: INK }}>Решение мэтчинга</h2>
              </div>
              <div className='d-flex flex-column' style={{ padding: '18px 28px', gap: 13 }}>
                {card.breakdown.map((pair) => (
                  <FactRow key={pair.label} label={pair.label}>
                    {pair.value}
                  </FactRow>
                ))}
                {/* Ниже ROLE_MANAGER бэкенд не отдаёт `raw` вовсе — раскрывать нечего. */}
                {card.raw && (
                  <div>
                    <button type='button' className='btn btn-sm btn-light' style={{ fontSize: 12.5 }} onClick={() => setRawOpen((o) => !o)}>
                      {rawOpen ? 'Скрыть сырые данные' : 'Показать сырые данные'}
                    </button>
                    {rawOpen && (
                      <pre style={{ ...RAW_BLOCK, margin: '8px 0 0', maxHeight: 420 }}>{JSON.stringify(card.raw, null, 2)}</pre>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </>
      )}
    </>
  )
}

export default FlowControlRefusalCardPage
